import { StatusDot } from "@/components/ui/status-dot";
import { useTimeAgo } from "@/hooks";
import { useRecentSpanSummary } from "@/lib/hooks/useRecentSpanSummary";
// import { formatShortDistance } from "@/lib/utils/date-utils";
import { Activity } from "lucide-react";
import { type ReactNode, useMemo } from "react";

interface TelemetrySummaryProps {
  projectId: string;
  windowMinutes?: number;
}

type TelemetryStatus = "active" | "error" | "idle";

const TELEMETRY_COLOR_MAP: Record<TelemetryStatus, string> = {
  active: "bg-success",
  error: "bg-destructive",
  idle: "bg-muted",
};

const DEFAULT_WINDOW_MINUTES = 15;

export function TelemetrySummary({
  projectId,
  windowMinutes = DEFAULT_WINDOW_MINUTES,
}: TelemetrySummaryProps) {
  const { summary, isLoading, error } = useRecentSpanSummary(
    projectId,
    windowMinutes,
  );

  const { spanCount, lastActivityAt, lastErrorAt } = summary;

  const telemetryStatus = useMemo<TelemetryStatus>(() => {
    if (error) {
      return "error";
    }

    return spanCount > 0 ? "active" : "idle";
  }, [error, spanCount]);

  const telemetryLabel = useMemo(() => {
    if (error) {
      return "Telemetry unavailable";
    }

    if (spanCount > 0) {
      const suffix = spanCount === 1 ? "" : "s";
      return `${spanCount} span${suffix} last ${windowMinutes}m`;
    }

    return `No spans last ${windowMinutes}m`;
  }, [error, spanCount, windowMinutes]);

  const telemetryDescription = useMemo(() => {
    if (isLoading) {
      return "Gathering recent activity…";
    }

    if (error) {
      return error.message;
    }

    if (spanCount === 0) {
      return "Trigger your MCP server to record telemetry.";
    }

    const parts: ReactNode[] = [];

    if (lastActivityAt) {
      parts.push(
        <>
          Last activity <Timeago date={lastActivityAt} />
        </>,
      );
    }

    if (lastErrorAt) {
      parts.push(
        <>
          Last error <Timeago date={lastErrorAt} />
        </>,
      );
    }

    return parts.length > 0 ? parts : undefined;
  }, [error, isLoading, lastActivityAt, lastErrorAt, spanCount]);

  return (
    <div className="flex flex-col items-end gap-1 text-right">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <Activity className="h-4 w-4" />
          <span>Recent activity:</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground">
          <StatusDot
            status={telemetryStatus}
            colorMap={TELEMETRY_COLOR_MAP}
            ariaLabel={`Telemetry status: ${telemetryLabel}`}
          />
          <span>{telemetryLabel}</span>
        </span>
      </div>
      {telemetryDescription ? (
        <div className="text-xs text-muted-foreground">
          {telemetryDescription}
        </div>
      ) : null}
    </div>
  );
}

function Timeago({ date }: { date: Date }) {
  const timeAgo = useTimeAgo(date.toISOString(), { strict: true });
  return <span>{timeAgo}</span>;
}
